import { Col, Row } from 'reactstrap';
import React, { useCallback, useState } from 'react';
import SearchInput from 'components/SearchInput';
import { useApiGet } from 'hooks/useApi';
import Pagination from 'models/Pagination';
import PaginationNav from 'components/Pagination';
import { Link, useNavigate } from 'react-router-dom';
import Icon from 'components/Icon';
import Table from 'components/Table';
import { Column } from 'components/Table/_presenters/props/tableComponentProps';
import AdminNotification from 'models/AdminNotification';
import Button from 'components/Button';
import ModalAdminImageNotification from 'components/ModalAdminImageNotification';

const AdminNotificationsListPage = () => {
  const navigate = useNavigate();
  const [filteredData, setFilteredData] = React.useState({
    pageSize: 10,
    page: 1,
    search: ''
  });
  const { data: adminNotificationsData, isLoading } = useApiGet<
    Pagination<AdminNotification>
  >('/admin/notifications', {
    params: filteredData
  });

  const handleSearch = useCallback(
    (search: string) => {
      setFilteredData({ ...filteredData, search });
    },
    [filteredData]
  );

  const handleClearFilter = useCallback(() => {
    setFilteredData({ pageSize: 10, page: 1, search: '' });
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      setFilteredData({ ...filteredData, page });
    },
    [filteredData]
  );

  const goToEdit = useCallback(
    (notification: AdminNotification) => {
      navigate(`/admin/notifications/${notification.id}`);
    },
    [navigate]
  );

  const [
    selectedAdminNotification,
    setSelectedAdminNotification
  ] = useState<AdminNotification | null>(null);

  const [modalOpen, setModalOpen] = useState(false);

  const toggleModal = () => setModalOpen((prevState) => !prevState);

  const handleNotificationClick = useCallback(
    (e: React.MouseEvent, notification: AdminNotification) => {
      e.stopPropagation();
      setSelectedAdminNotification(notification);
      toggleModal();
    },
    []
  );

  const columns: Column<AdminNotification>[] = [
    {
      accessor: 'title',
      label: 'Título',
      render: (title) => <span className="fw-medium">{String(title)}</span>
    },
    {
      accessor: 'message',
      label: 'Mensagem',
      render: (message) => (
        <span className="text-truncate-wrap">{String(message || '')}</span>
      )
    },
    {
      accessor: 'read',
      label: 'Status',
      render: (read) => (
        <span className={`badge ${read ? 'bg-success' : 'bg-warning'}`}>
          {read ? 'Lida' : 'Não lida'}
        </span>
      )
    },
    {
      accessor: 'image',
      label: 'Imagem',
      render: (image, notification) =>
        image ? (
          <Button
            type="button"
            color="transparent"
            icon="play-btn-fill"
            onClick={(e: React.MouseEvent) =>
              handleNotificationClick(e, notification)
            }
          />
        ) : (
          <span className="text-muted">-</span>
        )
    },
    {
      accessor: 'actions',
      label: 'Ações',
      render: () => <Icon icon="eye" className="text-primary" />
    }
  ];

  return (
    <div className="padding-page">
      <Row className="d-flex flex-column flex-md-row">
        <Col className="col-12 col-md">
          <div>
            <p className="text-gray-600">Administração</p>
            <h4 className="fw-bold">Notificações</h4>
            <span className="small-typography text-gray-600">
              Gerencie as notificações enviadas aos clientes.
            </span>
          </div>
        </Col>
        <Col className="col-12 col-md align-text mt-3 mt-md-0">
          <Link
            to="/admin/notifications/create"
            className="btn btn-primary text-white button-register"
          >
            Cadastrar
          </Link>
        </Col>
      </Row>
      <Row className="d-flex align-items-center mt-2 flex-column flex-md-row">
        <Col className="col-12 col-md">
          <SearchInput
            onClearFilter={handleClearFilter}
            onSearchTextChange={handleSearch}
            placeholder="Buscar notificação"
          />
        </Col>
      </Row>
      <Table
        data={adminNotificationsData?.items || []}
        columns={columns}
        isLoading={isLoading}
        onRowClick={goToEdit}
      />
      {selectedAdminNotification && (
        <ModalAdminImageNotification
          isOpen={modalOpen}
          toggle={toggleModal}
          adminNotification={selectedAdminNotification}
        />
      )}
      <div className="d-flex justify-content-between align-items-center flex-column flex-md-row mt-3">
        {adminNotificationsData && (
          <PaginationNav
            onChange={handlePageChange}
            totalPages={adminNotificationsData.totalPages}
            currentPage={adminNotificationsData.page}
          />
        )}
      </div>
    </div>
  );
};

export default AdminNotificationsListPage;
